import { inject, Injectable, signal } from '@angular/core';
import { ApiDataAccess } from '@softline/core';
import { SOFTLINE_API_DELIVERY_NOTE_QUERY_ICONS } from '../delivery-note-correction.api';
import {
  DocumentIconCode,
  DocumentIconsRequest,
  DocumentIconsResponse,
} from '../types/document-icons';

const EMPTY: readonly DocumentIconCode[] = Object.freeze([]);

@Injectable()
export class DocumentIconsService {
  private readonly dataAccess = inject(ApiDataAccess);

  private readonly cache = signal<Map<string, readonly DocumentIconCode[]>>(new Map());
  private readonly inFlight = new Map<string, Promise<void>>();

  get(documentId: string | null | undefined): readonly DocumentIconCode[] {
    if (!documentId) return EMPTY;
    return this.cache().get(documentId) ?? EMPTY;
  }

  async loadFor(documentIds: readonly string[]): Promise<void> {
    const cached = this.cache();
    const missing = documentIds.filter(
      (id) => !!id && !cached.has(id) && !this.inFlight.has(id)
    );
    if (missing.length === 0) return;

    const request: DocumentIconsRequest = { documentIds: missing };
    const promise = this.dataAccess
      .create<DocumentIconsRequest, DocumentIconsResponse>(
        { path: SOFTLINE_API_DELIVERY_NOTE_QUERY_ICONS },
        request
      )
      .then((response) => {
        this.cache.update((map) => {
          const next = new Map(map);
          const returned = new Set<string>();
          for (const entry of response?.documents ?? []) {
            next.set(entry.documentId, Object.freeze([...(entry.icons ?? [])]));
            returned.add(entry.documentId);
          }
          for (const id of missing) {
            if (!returned.has(id)) next.set(id, EMPTY);
          }
          return next;
        });
      })
      .catch(() => {
        this.cache.update((map) => {
          const next = new Map(map);
          for (const id of missing) {
            if (!next.has(id)) next.set(id, EMPTY);
          }
          return next;
        });
      })
      .finally(() => {
        for (const id of missing) this.inFlight.delete(id);
      });

    for (const id of missing) this.inFlight.set(id, promise);
    await promise;
  }

  async refresh(documentId: string): Promise<void> {
    this.cache.update((map) => {
      if (!map.has(documentId)) return map;
      const next = new Map(map);
      next.delete(documentId);
      return next;
    });
    await this.loadFor([documentId]);
  }
}
